// DEMO practice requests — client side.
//
// A brand-new DJ lands on a 100% empty Requests page and never learns where
// to click or how to swipe. The demo cards fix that: the REAL OfferCard
// (identical swipe/accept/decline mechanics) preloaded with a song, clearly
// labeled DEMO, walking the full real flow: pending → swipe accept → Queued →
// clear ("play") → Request History, with decline reachable from both pending
// and queued.
//
// TWO cards (founder, 1.5.1 #6), both in Pending from the start since 1.5.2
// b49. Card 1 is September. Card 2 ("Tití Me Preguntó") is
// DECLINE-ONLY (founder, build 44: "they need to decline at least one — so
// Tití needs to be declined"): September can go either way, Tití can't be
// accepted.
// "You got it!" fires once, when the last card in play is handled.
//
// They are NEVER Orders. They must never touch Orders/money/earnings/stats/
// notification paths — the server enforces the same rule from its side
// (tests/demo-request.test.js in the server repo scans for leaks). The only
// persistence is User.demoRequest (card 1) and User.demoRequest2 (card 2),
// each {status, queuedAt, handledAt}, advanced through
// PATCH /users/:slug/demo-request {which, status}, which makes "handled once =
// gone forever" hold across devices and reinstalls.
//
// Eligibility is server-computed (`showDemoRequest` / `showDemoRequest2` on
// GET /users/:slug): never interacted AND zero real requests ever (founder
// decision: dormant existing DJs with no requests get it too, not just new
// signups). Talent who answered card 1 before 1.5.1 never get card 2. An
// older server sends no showDemoRequest2 → card 1 only, exactly as before.

import { usersApi } from "../services/users";
import type { HistoryItem } from "../types/orders";
import type { User } from "../types/User";
import { t } from "../i18n";

export type DemoStatus = null | "queued" | "played" | "declined";
export type DemoWhich = 1 | 2;
/** The gesture a pending card asks for — the hint line and the peek follow it. */
export type DemoAsk = "accept" | "decline";
export interface DemoState {
  status: DemoStatus;
  queuedAt?: string | null;
  handledAt?: string | null;
}

// The songs on the cards. Card 1 uses the same cover asset as the Desktop
// Companion sample card (public/sample-request-cover.jpg), so the two stay in
// sync when the artwork updates; its trackId is the canonical "September"
// single. Card 2 is Bad Bunny's "Tití Me Preguntó" (Un Verano Sin Ti, 2022 —
// founder 10/11; before it "Never Gonna Give You Up", and "Baby Shark" before
// that) with Spotify's own cover URL, exactly what a real request carries.
// The id stays "demo-request-2", so a card already answered keeps its place
// in history. Ids are never Mongo ObjectIds — they cannot collide with orders.
export const DEMO_SONGS = {
  1: {
    id: "demo-request",
    trackId: "2grjqo0Frpf2okIBiifQKs",
    searchQuery: "September",
    trackArtists: ["Earth, Wind & Fire"],
    trackImage: "/sample-request-cover.jpg",
  },
  2: {
    id: "demo-request-2",
    trackId: "1IHWl5LamUGEuP4ozKQSXZ",
    searchQuery: "Tití Me Preguntó",
    trackArtists: ["Bad Bunny"],
    trackImage: "https://i.scdn.co/image/ab67616d0000b27349d694203245f241a1bcaa72",
  },
} as const;

export const DEMO_SUBLINE = t("demoRequest.practiceRequestNoReal");

/** Both cards as the account left them, plus what the server offers now. */
export interface DemoCards {
  1: DemoState;
  2: DemoState;
  offer1: boolean;
  offer2: boolean;
}

const stateOf = (d?: DemoState | null): DemoState => ({
  status: d?.status ?? null,
  queuedAt: d?.queuedAt ?? null,
  handledAt: d?.handledAt ?? null,
});

export const demoCardsFrom = (me: User): DemoCards => ({
  1: stateOf(me.demoRequest),
  2: stateOf(me.demoRequest2),
  offer1: !!me.showDemoRequest,
  offer2: !!me.showDemoRequest2,
});

const isHandled = (s: DemoStatus) => s === "played" || s === "declined";

/** The first card waiting in Pending (the one the hint and the nudge follow). */
export function pendingDemo(c: DemoCards): DemoWhich | null {
  return pendingDemos(c)[0] ?? null;
}

/** Every practice card waiting in Pending, card 1 first. Both show from the
    start (founder, 1.5.2 b49: "Bad Bunny comes in after accepting the first
    one — both should be there from the start"); the swipe hint and the nudge
    follow the first. */
export function pendingDemos(c: DemoCards): DemoWhich[] {
  const out: DemoWhich[] = [];
  if (c.offer1 && c[1].status === null) out.push(1);
  if (c.offer2 && c[2].status === null) out.push(2);
  return out;
}

/** Card 1 asks to accept; card 2 is decline-only (founder, build 44), so
    it always asks to decline. */
export function demoAsk(_c: DemoCards, which: DemoWhich): DemoAsk {
  return which === 1 ? "accept" : "decline";
}

/** Cards waiting in Queued, card 1 first. */
export const queuedDemos = (c: DemoCards): DemoWhich[] => ([1, 2] as const).filter((w) => c[w].status === "queued");

/** One answer, applied locally — advanceDemoRequest sends the same move. */
export function advanceDemo(c: DemoCards, which: DemoWhich, to: Exclude<DemoStatus, null>): DemoCards {
  const now = new Date().toISOString();
  const cur = c[which];
  const next: DemoState = {
    status: to,
    queuedAt: to === "queued" ? now : cur.queuedAt ?? null,
    handledAt: isHandled(to) ? now : cur.handledAt ?? null,
  };
  return { ...c, [which]: next };
}

/** True when this answer handled the last card in play: time for "You got it!". */
export const finishedDemos = (next: DemoCards, to: Exclude<DemoStatus, null>) =>
  isHandled(to) && pendingDemo(next) === null && queuedDemos(next).length === 0;

/** Handled cards as Request History rows. They never were Orders, so the
    /orders endpoints can't return them — the history bubble and modal merge
    these in by their real handledAt. Amount = the price the card showed. */
export function demoHistoryItems(c: DemoCards | null, amount?: number, currency?: string): HistoryItem[] {
  if (!c) return [];
  return ([1, 2] as const).flatMap((w) => {
    const { status, handledAt } = c[w];
    if (status !== "played" && status !== "declined") return [];
    const song = DEMO_SONGS[w];
    return [
      {
        _id: song.id,
        searchQuery: song.searchQuery,
        trackId: song.trackId,
        trackImage: song.trackImage,
        trackArtists: [...song.trackArtists],
        amount: amount ?? 0,
        currency,
        status,
        createdAt: handledAt || "",
        updatedAt: handledAt || "",
        isDemo: true,
      },
    ];
  });
}

// Advance one card's per-account lifecycle. Fire-and-forget by design: the UI
// advances optimistically and this call syncs the account. If it fails
// (offline, old server without the route), we silently stay on the client
// state for this session — the next profile fetch re-syncs, and the worst
// case is the demo card showing again later. Never an error screen
// (CLAUDE.md: prefer silent recovery).
export async function advanceDemoRequest(
  slug: string,
  status: Exclude<DemoStatus, null>,
  which: DemoWhich = 1
): Promise<void> {
  try {
    await usersApi.advanceDemo(slug, which, status);
  } catch {
    /* silent — see above */
  }
}
